import { useMutation, useQuery } from '@apollo/client';
import { Box, Flex } from '@theme-ui/components';
import Cookies from 'js-cookie';
import type { ChangeEventHandler } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { AlertMessage, AlertTitle, AlertType } from '@app/components/Alert';
import Alerts from '@app/components/Alerts';
import { Button } from '@app/components/Button';
import CheckoutBackLink from '@app/components/CheckoutBackLink';
import { RadioBoxLabel, RadioBoxRadio } from '@app/components/RadioBox';
import { H2 } from '@app/components/typography';
import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';
import { SHOP_STORE_CODE_COOKIE } from '@app/helpers/localization';
import { logGraphqlClientError } from '@app/helpers/logGraphqlClientError';
import { isNotNullish } from '@app/helpers/predicate';
import { pushPerpetualEcommerceEvent } from '@app/helpers/tracking';
import useAlerts from '@app/hooks/useAlerts';
import { useFireOnce } from '@app/hooks/useFireOnce';
import useGTMTracking from '@app/hooks/useGTMTracking';
import useSimpleCart from '@app/hooks/useSimpleCart';
import { APP_BASE_URL } from '@config/common.config';
import messages from './Payment.messages';
import {
  PAYMENT_METHODS_QUERY,
  PLACE_ORDER_MUTATION,
  SET_PAYMENT_METHOD_MUTATION,
} from './Payment.queries';
import PaymentOption from './PaymentOption/PaymentOption';
import type {
  PaymentMethodsQuery,
  PaymentMethodsQueryVariables,
} from './__generated__/PaymentMethodsQuery';
import type {
  PlaceOrderMutation,
  PlaceOrderMutationVariables,
} from './__generated__/PlaceOrderMutation';
import type {
  SetPaymentMethodMutation,
  SetPaymentMethodMutationVariables,
} from './__generated__/SetPaymentMethodMutation';
import { useCheckPlaceOrderStatusQuery } from './__graphql__/Payment.queries';

const FREE_PAYMENT_METHOD_CODE = 'free';

const Payment = () => {
  const { language, status } = useParams<{
    language: string;
    status?: string;
  }>();
  const { alerts, addAlerts, clearAlerts } = useAlerts();
  const [selectedMethodCode, setSelectedMethodCode] = useState<
    string | undefined
  >();
  const [redirecting, setRedirecting] = useState(false);
  const storeCode = Cookies.get(SHOP_STORE_CODE_COOKIE);
  const { cartId } = useGetCartId();
  const {
    items,
    prices,
    isVirtual,
    storeConfig,
    appliedCoupons,
    selectedShippingMethod,
  } = useSimpleCart();
  const fireEventOnce = useFireOnce();

  const { data, loading: methodsLoading } = useQuery<
    PaymentMethodsQuery,
    PaymentMethodsQueryVariables
  >(PAYMENT_METHODS_QUERY, {
    variables: {
      storeCode: storeCode || '',
      isVirtualCart: Boolean(isVirtual),
    },
    skip: !storeCode || !cartId,
    ssr: false,
    onError: (error) => {
      addAlerts([genericErrorAlert({ message: error.message })]);
    },
  });

  useEffect(() => {
    const paymentFailed =
      status === PLACE_ORDER_STATUS_FAILED || Boolean(alerts.length);

    if (!paymentFailed && prices?.grandTotal?.value === 0) {
      setPaymentMethodAndPlaceOrder({
        variables: { cartId: cartId ?? '', code: FREE_PAYMENT_METHOD_CODE },
      });
    }
  }, [prices, cartId, status, alerts.length]);

  useGTMTracking({ step: 'payment', items, prices, storeConfig });

  const [placeOrder, { loading: placeOrderLoading }] = useMutation<
    PlaceOrderMutation,
    PlaceOrderMutationVariables
  >(PLACE_ORDER_MUTATION, {
    variables: {
      cartId: cartId ?? '',
      returnUrl: getReturnUrl(language),
      successUrl: getSuccessUrl(language, cartId ?? ''),
      failedUrl: getFailedUrl(language),
      origin: 'Native Instruments',
    },
    onCompleted: (data) => {
      const { payRedirectUrl } = data?.magento2?.redirectAndPlaceOrder ?? {};

      if (payRedirectUrl) {
        setRedirecting(true);
        window.location.href = payRedirectUrl;
      }
    },
    onError: (error) => {
      setRedirecting(false);
      addAlerts([genericErrorAlert({ message: error.message })]);
    },
  });

  const [setPaymentMethodAndPlaceOrder, { loading: setMethodLoading }] =
    useMutation<SetPaymentMethodMutation, SetPaymentMethodMutationVariables>(
      SET_PAYMENT_METHOD_MUTATION,
      {
        variables: { cartId: cartId ?? '', code: selectedMethodCode ?? '' },
        onCompleted: (data) => {
          const cartMethodCode =
            data?.magento2?.setPaymentMethodOnCart?.cart?.selectedPaymentMethod
              ?.code;

          if (cartMethodCode) placeOrder();
        },
        onError: (error) => {
          addAlerts([genericErrorAlert({ message: error.message })]);
        },
      },
    );

  const {
    availablePaymentMethods: cartAvailablePaymentMethods,
    selectedPaymentMethod: cartSelectedPaymentMethod,
  } = data?.magento2?.customerCart ?? {};

  const availablePaymentMethods = useMemo(
    () =>
      cartAvailablePaymentMethods
        ?.filter(isNotNullish)
        // Since Magento returns all enabled gateway payment methods,
        // we need to exclude Adyen payment methods on NI v1 for the transition phase.
        .filter((method) => !method.code.includes('adyen_')),
    [cartAvailablePaymentMethods],
  );

  const cartSelectedMethodCode = cartSelectedPaymentMethod?.code;

  const { type: previousMethodCode } =
    data?.magento2?.customer?.globalOrders?.items?.[0]?.paymentMethods?.[0] ??
    {};

  const isValidMethodCode = useCallback(
    (code?: string): code is string =>
      Boolean(availablePaymentMethods?.find((method) => method.code === code)),
    [availablePaymentMethods],
  );

  // Whenever a payment fails, we need to check order status
  // to be able to gather and capture the error message.
  const { data: orderStatusData, error: orderStatusError } =
    useCheckPlaceOrderStatusQuery({
      variables: { cartId: cartId ?? '' },
      skip: !cartId || !(status === 'failed'),
      fetchPolicy: 'network-only',
      ssr: false,
    });

  const orderErrors = orderStatusData?.magento2?.checkPlaceOrderStatus?.errors;

  useEffect(() => {
    fireEventOnce(() => {
      pushPerpetualEcommerceEvent('add_payment_info', {
        cart_id: cartId,
        currency: prices?.grandTotal?.currency,
        shipping_tier: selectedShippingMethod?.methodCode || '',
        shipping: selectedShippingMethod?.amountInclTax.value,
        coupons: appliedCoupons,
        value: prices?.grandTotal?.value,
        items: items?.map((item) => ({
          // item_id/item_name should come from the parent product,
          // but it's not available in the cart item data yet.
          // @TODO [ECOM-85, ECOM-84]
          item_id: item?.product?.sku,
          item_name: item?.product?.name,
          sku_id: item?.product?.sku,
          sku_name: item?.product?.name,
          sdbs_id: item?.product?.sdbsId,
          price: item?.prices?.price.value || 0,
          quantity: item?.quantity,
        })),
      });
    });
  }, [
    cartId,
    items,
    prices,
    selectedShippingMethod,
    appliedCoupons,
    fireEventOnce,
  ]);

  useEffect(() => {
    if (orderStatusError || orderErrors?.length) {
      logGraphqlClientError({
        errors: [
          `Payment failed for cart ${cartId}: ${
            orderStatusError?.message || orderErrors
          }`,
        ],
        operationName: 'CheckPlaceOrderStatus',
      });
    }
  }, [cartId, orderErrors, orderStatusError]);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target }) => {
      clearAlerts();
      setSelectedMethodCode(target.value);
    },
    [clearAlerts],
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      clearAlerts();

      if (!cartId) throw new Error('Cannot submit payment without cart id');

      if (!selectedMethodCode) {
        addAlerts([
          genericErrorAlert({ message: <RequiredValidationError /> }),
        ]);

        return;
      }

      if (selectedMethodCode === cartSelectedMethodCode) placeOrder();
      else setPaymentMethodAndPlaceOrder();
    },
    [
      cartId,
      selectedMethodCode,
      cartSelectedMethodCode,
      setPaymentMethodAndPlaceOrder,
      placeOrder,
      addAlerts,
      clearAlerts,
    ],
  );

  useEffect(() => {
    if (prices?.grandTotal?.value === 0) {
      setSelectedMethodCode(FREE_PAYMENT_METHOD_CODE);
    } else {
      setSelectedMethodCode((currentCode) => {
        const [validCurrentCode, validCartCode, validPreviousCode] = [
          currentCode,
          cartSelectedMethodCode,
          previousMethodCode,
        ].map((code) => (isValidMethodCode(code) ? code : undefined));

        return validCurrentCode || validCartCode || validPreviousCode;
      });
    }
  }, [cartSelectedMethodCode, previousMethodCode, isValidMethodCode, prices]);

  useEffect(() => {
    if (status === PLACE_ORDER_STATUS_FAILED) {
      addAlerts([
        {
          type: AlertType.Error,
          persist: true,
          content: (
            <>
              <AlertTitle>
                <FormattedMessage {...messages.paymentFailedErrorTitle} />
              </AlertTitle>
              <AlertMessage>
                <FormattedMessage {...messages.paymentFailedErrorMessage} />
              </AlertMessage>
            </>
          ),
        },
      ]);
    }
  }, [status, addAlerts]);

  const isLoading =
    methodsLoading || setMethodLoading || placeOrderLoading || redirecting;

  if (!cartId) return null;

  return (
    <>
      <H2 as="h1">
        <FormattedMessage {...messages.title} />
      </H2>
      <Box mt={4}>
        {Boolean(alerts.length) && (
          <Box data-testid="alerts" mb={3}>
            <Alerts alerts={alerts} />
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          {availablePaymentMethods?.map(({ code, title }) => (
            <Fragment key={code}>
              <RadioBoxRadio
                id={code}
                value={code}
                checked={code === selectedMethodCode}
                onChange={handleChange}
                disabled={isLoading}
                outlineChecked
              />
              <RadioBoxLabel
                data-testid={`payment-option-${code}`}
                htmlFor={code}
              >
                <PaymentOption code={code} title={title} />
              </RadioBoxLabel>
            </Fragment>
          ))}
          <Flex
            mt={4}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <CheckoutBackLink to="/:language/shop/checkout/review">
              <FormattedMessage {...messages.back} />
            </CheckoutBackLink>
            <Button
              type="primary"
              isLoading={isLoading}
              disabled={isLoading}
              onClick={handleSubmit}
              data-testid="place-order-button"
            >
              <FormattedMessage {...messages.continue} />
            </Button>
          </Flex>
        </form>
      </Box>
    </>
  );
};

export default Payment;

const genericErrorAlert = ({
  message,
  persist = false,
}: {
  message: React.ReactNode;
  persist?: boolean;
}) => ({
  persist,
  type: AlertType.Error,
  content: (
    <>
      <AlertTitle>
        <FormattedMessage {...messages.alertErrorTitle} />
      </AlertTitle>
      <AlertMessage>{message}</AlertMessage>
    </>
  ),
});

const RequiredValidationError = () => (
  <FormattedMessage
    {...messages.requiredValidationError}
    values={{
      field: <FormattedMessage {...messages.paymentMethodRequiredError} />,
    }}
  />
);

export const PLACE_ORDER_STATUS_FAILED = 'failed';

export const getReturnUrl = (language: string): string =>
  `${APP_BASE_URL}/${language}/shop/checkout/payment/`;

export const getSuccessUrl = (language: string, cartId: string): string =>
  `${APP_BASE_URL}/${language}/shop/checkout/response/${cartId}/`;

export const getFailedUrl = (language: string): string =>
  `${getReturnUrl(language)}${PLACE_ORDER_STATUS_FAILED}/`;
